import React from 'react';
import {
  Box, Button, Flex, Text,
} from '@chakra-ui/react';
import { countWord } from '../../utils/tenantAccess';

export const NAVY = '#0b2a78';

export const navyPress = {
  _hover: { bg: NAVY, color: 'white' },
  _active: { bg: NAVY, color: 'white' },
};

export const EmptyState = ({ title, text }) => (
  <Box textAlign="center" py={7} px={4} color="gray.600">
    <Text fontWeight="600" color="gray.800">{title}</Text>
    <Text fontSize="sm" maxW="44ch" mx="auto">{text}</Text>
  </Box>
);

export const Panel = ({
  title, subtitle, children, footer,
}) => (
  <Box as="section" borderWidth="1px" borderRadius="md" bg="white" minW="0">
    <Box px={5} py={4} borderBottomWidth="1px">
      <Text as="h2" fontSize="lg" fontWeight="700" color="blue.900">{title}</Text>
      <Text fontSize="sm" color="gray.600" mt={1}>{subtitle}</Text>
    </Box>
    <Flex direction="column" gap={4} px={5} py={4}>{children}</Flex>
    {footer}
  </Box>
);

export const StepFooter = ({
  showBack, hint, canContinue, onBack, onContinue,
}) => (
  <Flex
    px={5}
    py={3}
    gap={3}
    align="center"
    wrap="wrap"
    borderTopWidth="1px"
    bg="gray.50"
    borderBottomRadius="md"
  >
    {showBack && (
      <Button variant="outline" colorScheme="blue" onClick={onBack}>Back</Button>
    )}
    <Box flex="1" />
    {hint && <Text fontSize="xs" color="gray.600">{hint}</Text>}
    <Button
      colorScheme="blue"
      color={NAVY}
      {...navyPress}
      onClick={onContinue}
      isDisabled={!canContinue}
    >
      Continue
    </Button>
  </Flex>
);

const stepMeta = (step, { userCount, tenantCount, appCount }) => {
  const labels = [
    ['Users', userCount ? countWord(userCount, 'selected', 'selected') : 'Who to add'],
    ['Tenants', tenantCount ? countWord(tenantCount, 'selected', 'selected') : 'Where to add them'],
    ['App roles', appCount ? countWord(appCount, 'app') : 'Role per app'],
    ['Review', 'Confirm and run'],
  ];
  return labels[step - 1];
};

export const Stepper = ({
  step, canReach, onStep, counts,
}) => (
  <Flex as="ol" listStyleType="none" m="22px 0 18px" p={0} wrap="wrap">
    {[1, 2, 3, 4].map((number) => {
      const [label, subtitle] = stepMeta(number, counts);
      const done = number < step;
      const current = number === step;
      const enabled = canReach(number);
      return (
        <Box as="li" key={label} flex="1" minW="130px">
          <Button
            variant="ghost"
            width="100%"
            justifyContent="flex-start"
            borderRadius="0"
            borderBottomWidth="3px"
            borderBottomColor={done ? 'blue.700' : current ? 'blue.500' : 'gray.200'}
            color={current ? NAVY : 'gray.600'}
            _hover={{
              bg: NAVY,
              color: 'white',
              '& *': { color: 'white' },
            }}
            _active={{
              bg: NAVY,
              color: 'white',
              '& *': { color: 'white' },
            }}
            isDisabled={!enabled}
            aria-current={current ? 'step' : undefined}
            onClick={() => onStep(number)}
          >
            <Box textAlign="left">
              <Text fontWeight="600" fontSize="sm">{done ? `✓ ${label}` : `${number}. ${label}`}</Text>
              <Text fontWeight="400" fontSize="xs" color="gray.500">{subtitle}</Text>
            </Box>
          </Button>
        </Box>
      );
    })}
  </Flex>
);

export const SummaryPanel = ({ users, tenants, appRoles }) => {
  const roles = Object.entries(appRoles);
  const total = users.length * tenants.length * roles.length;
  return (
    <Box
      as="aside"
      borderWidth="1px"
      borderRadius="md"
      bg="gray.50"
      p={4}
      position="sticky"
      top="16px"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color="gray.600">
        This assignment
      </Text>
      <Box>
        <Flex justify="space-between">
          <Text color="gray.600">Users</Text>
          <Text fontWeight="700">{users.length}</Text>
        </Flex>
        {users.length > 0 && (
          <Box as="ul" pl={4} fontSize="xs" color="gray.600" maxH="110px" overflow="auto">
            {users.map((user) => (
              <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
            ))}
          </Box>
        )}
      </Box>
      <Box>
        <Flex justify="space-between">
          <Text color="gray.600">Tenants</Text>
          <Text fontWeight="700">{tenants.length}</Text>
        </Flex>
        {tenants.length > 0 && (
          <Box as="ul" pl={4} fontSize="xs" color="gray.600" maxH="110px" overflow="auto">
            {tenants.map((tenant) => (
              <li key={tenant.id}>{`${tenant.name}${tenant.inactive ? ' (inactive)' : ''}`}</li>
            ))}
          </Box>
        )}
      </Box>
      <Box>
        <Flex justify="space-between">
          <Text color="gray.600">App roles</Text>
          <Text fontWeight="700">{roles.length || <Text as="span" fontWeight="400" color="gray.500">Not chosen</Text>}</Text>
        </Flex>
        {roles.length > 0 && (
          <Box as="ul" pl={4} fontSize="xs" color="gray.600" maxH="110px" overflow="auto">
            {roles.map(([app, role]) => <li key={app}>{`${app}: ${role}`}</li>)}
          </Box>
        )}
      </Box>
      <Flex justify="space-between" align="baseline" borderTopWidth="1px" pt={3}>
        <Text color="gray.600">User × tenant × app</Text>
        <Text fontSize="2xl" fontWeight="700" color="blue.900">{total}</Text>
      </Flex>
      <Text fontSize="xs" color="gray.600">
        Adds people to tenants and sets roles. It never removes anyone from a tenant or app.
      </Text>
    </Box>
  );
};
